// Serialize an AI-chat generated build into a compact, machine-friendly string
// inserted into the Gemini transcript on subsequent turns. The text is NOT shown
// to the user — the chat card stays as-is. Goal: let the model answer
// slot-specific questions ("quelle est la cape sur ce stuff?") and reference
// items by id without hallucinating.

import { SLOT_IDX } from '../core/Build';
import { CLASSES } from '../core/characterUtils';
import { suggestAiBuildName } from './suggestAiBuildName';

const SLOT_LABELS = {
    [SLOT_IDX.HAT]: { fr: 'coiffe', en: 'hat', de: 'Haube', es: 'tocado', pt: 'chapéu' },
    [SLOT_IDX.CLOAK]: { fr: 'cape', en: 'cloak', de: 'Umhang', es: 'capa', pt: 'capa' },
    [SLOT_IDX.AMULET]: { fr: 'amulette', en: 'amulet', de: 'Amulett', es: 'amuleto', pt: 'amuleto' },
    [SLOT_IDX.BELT]: { fr: 'ceinture', en: 'belt', de: 'Gürtel', es: 'cinturón', pt: 'cinto' },
    [SLOT_IDX.RING1]: { fr: 'anneau 1', en: 'ring 1', de: 'Ring 1', es: 'anillo 1', pt: 'anel 1' },
    [SLOT_IDX.RING2]: { fr: 'anneau 2', en: 'ring 2', de: 'Ring 2', es: 'anillo 2', pt: 'anel 2' },
    [SLOT_IDX.BOOTS]: { fr: 'bottes', en: 'boots', de: 'Stiefel', es: 'botas', pt: 'botas' },
    [SLOT_IDX.WEAPON]: { fr: 'arme', en: 'weapon', de: 'Waffe', es: 'arma', pt: 'arma' },
    [SLOT_IDX.SHIELD]: { fr: 'bouclier', en: 'shield', de: 'Schild', es: 'escudo', pt: 'escudo' },
    [SLOT_IDX.PET]: { fr: 'familier', en: 'pet', de: 'Vertrauter', es: 'mascota', pt: 'mascote' },
    10: { fr: 'dofus/trophée 1', en: 'dofus/trophy 1', de: 'Dofus/Trophäe 1', es: 'dofus/trofeo 1', pt: 'dofus/troféu 1' },
    11: { fr: 'dofus/trophée 2', en: 'dofus/trophy 2', de: 'Dofus/Trophäe 2', es: 'dofus/trofeo 2', pt: 'dofus/troféu 2' },
    12: { fr: 'dofus/trophée 3', en: 'dofus/trophy 3', de: 'Dofus/Trophäe 3', es: 'dofus/trofeo 3', pt: 'dofus/troféu 3' },
    13: { fr: 'dofus/trophée 4', en: 'dofus/trophy 4', de: 'Dofus/Trophäe 4', es: 'dofus/trofeo 4', pt: 'dofus/troféu 4' },
    14: { fr: 'dofus/trophée 5', en: 'dofus/trophy 5', de: 'Dofus/Trophäe 5', es: 'dofus/trofeo 5', pt: 'dofus/troféu 5' },
    15: { fr: 'dofus/trophée 6', en: 'dofus/trophy 6', de: 'Dofus/Trophäe 6', es: 'dofus/trofeo 6', pt: 'dofus/troféu 6' },
};

// The transcript is prompt text for the AI backend, which only speaks fr/en
// (see backend/ai/gemini.py). Deliberately collapses de/es/pt to en rather
// than emitting a language the prompt pipeline cannot handle.
function normalizeLang(lang) {
    return lang === 'fr' ? 'fr' : 'en';
}

function localizedItemName(item, lang) {
    if (!item) return null;
    const raw = item.name;
    if (raw && typeof raw === 'object') {
        return raw[lang] || raw.fr || raw.en || null;
    }
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return null;
}

function localizedItemTypeName(item, lang) {
    const t = item?.type;
    if (!t) return null;
    if (t.name && typeof t.name === 'object') {
        return t.name[lang] || t.name.fr || t.name.en || null;
    }
    return null;
}

function classLabelFromBuild(build, lang) {
    const cls = typeof build?.getCharacterClass === 'function'
        ? build.getCharacterClass()
        : build?.characterClass;
    if (!cls) return null;
    if (cls.label && typeof cls.label === 'object') {
        return cls.label[lang] || cls.label.fr || cls.label.en || cls.name || null;
    }
    if (typeof cls === 'string') {
        const row = CLASSES.find((c) => c.name === cls);
        if (row?.label) return row.label[lang] || row.label.fr || row.label.en;
        return cls;
    }
    return cls.name || null;
}

function slotLabel(slotIdx, item, lang) {
    // For trophus slots, prefer the item type name (Dofus / Trophée / Prysmaradite)
    // because all 6 slots share a generic label otherwise.
    if (slotIdx >= 10 && slotIdx <= 15) {
        const typeName = localizedItemTypeName(item, lang);
        const slotNum = slotIdx - 9; // 1..6
        if (typeName) return `${typeName} ${slotNum}`;
    }
    return SLOT_LABELS[slotIdx]?.[lang] || SLOT_LABELS[slotIdx]?.en || `slot ${slotIdx}`;
}

// Returns a compact multi-line string. Empty string when build is unusable.
export function serializeBuildForTranscript({ build, draft, lang, userRequest } = {}) {
    if (!build || !Array.isArray(build.items)) return '';
    const L = normalizeLang(lang);

    const name = suggestAiBuildName({ draft, lang: L, userRequest: userRequest || '' });
    const cls = classLabelFromBuild(build, L);
    const lvl = Number(build.lvl) || null;

    const headerBits = [];
    if (cls) headerBits.push(L === 'fr' ? `classe ${cls}` : `class ${cls}`);
    if (lvl) headerBits.push(L === 'fr' ? `niv. ${lvl}` : `lvl ${lvl}`);
    const header = headerBits.length ? ` — ${headerBits.join(', ')}` : '';

    const tag = L === 'fr' ? '[STUFF GÉNÉRÉ]' : '[GENERATED BUILD]';
    const equipLabel = L === 'fr' ? 'Équipement' : 'Equipment';

    const lines = [`${tag} ${name}${header}.`, `${equipLabel} :`];
    build.items.forEach((item, idx) => {
        if (!item || !item.id) return;
        const label = slotLabel(idx, item, L);
        const itemName = localizedItemName(item, L) || `Item ${item.id}`;
        lines.push(`- ${label} : ${itemName} (id ${item.id})`);
    });

    if (lines.length === 2) {
        // No equipped items — emit a minimal note rather than a half-empty block.
        return `${tag} ${name}${header}.`;
    }
    return lines.join('\n');
}

// Marker inserted when the user opens / saves a generated build to their library.
// Carries the build_id so future tools (PR 3) can scope queries to it.
export function serializeBuildOpenedMarker({ build, draft, lang, buildId, userRequest } = {}) {
    const L = normalizeLang(lang);
    const name = suggestAiBuildName({ draft, lang: L, userRequest: userRequest || '' });
    if (L === 'fr') {
        return buildId
            ? `[STUFF SAUVEGARDÉ] L'utilisateur a ajouté « ${name} » à sa bibliothèque (build_id ${buildId}).`
            : `[STUFF SAUVEGARDÉ] L'utilisateur a ajouté « ${name} » à sa bibliothèque.`;
    }
    return buildId
        ? `[BUILD SAVED] The user added "${name}" to their library (build_id ${buildId}).`
        : `[BUILD SAVED] The user added "${name}" to their library.`;
}
